<template lang="pug">
    Tabs.customer-groups-tabs.pagination
        el-card.contains-table.customer-groups(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .customer-groups-table(v-else)
                paginated-items(
                    scope="ex"
                    type="shop_customer_group"
                    :loading="loading"
                    :items="customerGroupsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="customerGroupsPaginated.variables"
                    :all-columns="customerGroupsPaginated.allColumns"
                    :active-columns="customerGroupsPaginated.activeColumns"
                    :page-info="customerGroupsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @click="openModal"
                    @change-sort="handleSortInputChange"
                )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .row
                                .left
                                    el-button(
                                        v-if="!sharedShopDataReadOnly"
                                        type="primary"
                                        size="mini"
                                        icon="el-icon-fa-plus fas"
                                        @click="openModal()"
                                    )
                                        | {{ $t('New Customer Group') }}
                                .right.hide-on-empty
                                    el-button(
                                        plain
                                        size="mini"
                                        type="primary"
                                        icon="el-icon-fa-filter fas"
                                        @click="toggleFilterVisibility"
                                        :disabled="disable.showFilters || hasFilters"
                                    )
                                        span(v-if="showFilters") {{ $t('Hide filters') }}
                                        span(v-else) {{ $t('Show filters') }}
                    template(v-slot:default="{ value }")
                        el-tag(v-if="value" size="mini" type="success") {{ $t('Yes') }}
                        el-tag(v-else size="mini" type="info") {{ $t('No') }}
                    template(v-slot:currency="{ item }")
                        span(v-if="item.currency") {{ item.currency.iso4217 }}
                            span(v-if="item.currency.sign")  ({{ item.currency.sign }})
                    template(v-slot:actions="{ item }")
                        el-button.delete-button(
                            v-if="!sharedShopDataReadOnly"
                            plain
                            circle
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-alt fas"
                            :disabled="!!item.default"
                            :title="item.default ? $t('The default group cannot be deleted') : ''"
                            @click.stop.prevent="openDeleteDialog(item)"
                        )

        customer-group-form(
            :data="modal.data"
            :visibility.sync="modal.visibility"
            :saving="modal.saving"
            @submit="saveCustomerGroup"
            @delete="(id) => openDeleteDialog({ id, default: !!modal.data.default })"
            @closed="resetModal"
        )

        el-dialog(
            :title="$t('Delete Customer Group')"
            :visible.sync="deleteDialog.visibility"
            :close-on-click-modal="false"
            top="10vh"
            width="460px"
        )
            .body(v-loading="deleteDialog.loading")
                p {{ $t('Do you really want to delete the customer group?') }}
            template(slot="footer")
                .modal-footer
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            :disabled="deleteDialog.loading"
                            @click="closeDeleteDialog"
                        ) {{ $t('Cancel') }}
                        el-button(
                            size="mini"
                            type="danger"
                            icon="el-icon-fa-trash-alt fas"
                            :disabled="deleteDialog.loading"
                            @click="confirmDelete"
                        ) {{ $t('Delete') }}
</template>

<i18n>
{
    "de": {
        "New Customer Group": "Neue Kundengruppe",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Yes": "Ja",
        "No": "Nein",
        "Delete Customer Group": "Kundengruppe löschen",
        "Do you really want to delete the customer group?": "Möchten Sie die Kundengruppe wirklich löschen?",
        "Cancel": "Abbrechen",
        "Delete": "Löschen",
        "The customer group has been saved successfully": "Die Kundengruppe wurde erfolgreich gespeichert",
        "The customer group could not be saved": "Die Kundengruppe konnte nicht gespeichert werden",
        "The customer group has been deleted successfully": "Die Kundengruppe wurde erfolgreich gelöscht",
        "The customer group could not be deleted": "Die Kundengruppe konnte nicht gelöscht werden",
        "Customer group list could not be fetched": "Die Kundengruppenliste konnte nicht abgerufen werden",
        "The default group cannot be deleted": "Die Standardgruppe kann nicht gelöscht werden"
    }
}
</i18n>

<script>
import _ from 'lodash';
import pinia from '@/pinia';
import { useSetupStore, useShopCustomerGroupStore } from '@/stores';
import { adminClient as apolloClient } from '@/store/apollo';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error';
import Tabs from '@/extensions/restrictedArea/views/Partials/Tabs';
import CustomerGroupForm from '@/extensions/shop/views/CustomerGroup/Form';

export default {
    name: 'CustomerGroupsList',
    components: {
        PaginatedItems,
        Error,
        Tabs,
        CustomerGroupForm,
    },
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            },
            modal: {
                visibility: false,
                saving: false,
                data: {},
            },
            deleteDialog: {
                visibility: false,
                loading: false,
                target: null,
            },
        };
    },
    computed: {
        setupStore() {
            return useSetupStore(pinia);
        },
        customerGroupStore() {
            return useShopCustomerGroupStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        loading() {
            return this.customerGroupStore.loading;
        },
        customerGroupsPaginated() {
            return this.customerGroupStore.customerGroupsPaginated;
        },
        filters() {
            const filters = _.cloneDeep(this.customerGroupsPaginated.filters || {});
            delete filters.cms;
            return filters;
        },
        showFilters() {
            return this.hasFilters || _.get(this.customerGroupsPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        sort() {
            const variables = this.customerGroupsPaginated.variables;
            const defaultSort = this.customerGroupsPaginated.defaultSort || { field: 'id', direction: 'desc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction };
        },
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) this.initialize();
        },
    },
    mounted() {
        this.initialize();
    },
    methods: {
        initialize() {
            // Drop any items left over from a prior CMS / prior mount.
            // Otherwise the table briefly shows rows from the previous shop
            // underneath the loading spinner, and clicking edit on one of
            // them submits a save with a currency_id that belongs to a
            // different shop — the mutation then 404s on Currency lookup.
            this.customerGroupStore.customerGroupsPaginated.items = [];
            this.updateLoading(true);
            this.refetch();
        },
        refetch() {
            this.updateLoading(true);
            const cmsId = _.get(this.activeCms, 'id');
            if (cmsId) {
                this.customerGroupStore.defineFilterValue({
                    key: 'cms',
                    value: { key: 'cms', match: cmsId, like: '', from: '', to: '', in: [] },
                });
            }
            return this.customerGroupStore.fetchCustomerGroupsPaginated()
                .then(() => this.updateLoading(false))
                .catch((err) => {
                    this.handleFetchError(err);
                    this.updateLoading(false);
                });
        },
        debouncedRefetch: _.debounce(function () {
            this.refetch();
        }, 300),
        handleFetchError(err) {
            const graphQLErrors = _.get(err, 'graphQLErrors');
            const code = _.get(graphQLErrors, '0.code') || 501;
            const message = _.get(graphQLErrors, '0.message') || 'An error occurred!';

            if (code === 401) {
                this.error = { code, message: 'You are not authorized' };
                return;
            }
            if (code === 403) {
                this.error = { code, message: 'You are not allowed to be here' };
                return;
            }

            this.error = true;
            this.$notify.error({
                title: this.$t(message),
                message: this.$t('Customer group list could not be fetched'),
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
            console.error(err);
        },
        updateLoading(value) {
            this.customerGroupStore.updateLoading(value);
        },
        handleSizeChange(perPage) {
            this.customerGroupStore.defineItemsPerPage(perPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.customerGroupStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.customerGroupStore.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.customerGroupStore.defineCurrentPage(1);
            this.customerGroupStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.customerGroupStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisibility() {
            const value = !this.showFilters;
            this.disable.showFilters = true;
            this.customerGroupStore.defineFilterVisibility(value);
            this.setupStore.saveFilterVisibility({ scope: 'ex', key: 'shop_customer_group', value })
                .finally(() => { this.disable.showFilters = false; });
        },
        openModal(group = null) {
            this.modal.data = group
                ? {
                    id: group.id,
                    key: group.key,
                    name: group.name,
                    description: group.description,
                    default: !!group.default,
                    guest_eligible: !!group.guest_eligible,
                    currency: group.currency ? { id: group.currency.id, iso4217: group.currency.iso4217, sign: group.currency.sign } : { id: null },
                }
                : {};
            this.modal.visibility = true;
        },
        resetModal() {
            this.modal.visibility = false;
            this.modal.saving = false;
            this.modal.data = {};
        },
        async saveCustomerGroup(payload) {
            this.modal.saving = true;
            try {
                await this.customerGroupStore.save(payload);
                this.$notify.success({
                    title: this.$t('The customer group has been saved successfully'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
                this.modal.visibility = false;
                this.refetch();
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('The customer group could not be saved'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.modal.saving = false;
            }
        },
        openDeleteDialog(group) {
            if (group.default) {
                this.$notify.warning({
                    title: this.$t('The default group cannot be deleted'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 5000,
                });
                return;
            }
            this.deleteDialog.target = group;
            this.deleteDialog.visibility = true;
        },
        closeDeleteDialog() {
            this.deleteDialog.visibility = false;
            this.deleteDialog.loading = false;
            this.deleteDialog.target = null;
        },
        async confirmDelete() {
            const target = this.deleteDialog.target;
            if (!target) return;
            this.deleteDialog.loading = true;
            try {
                await this.customerGroupStore.delete(target.id);
                this.$notify.success({
                    title: this.$t('The customer group has been deleted successfully'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
                this.modal.visibility = false;
                this.closeDeleteDialog();
                this.refetch();
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('The customer group could not be deleted'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                this.deleteDialog.loading = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.customer-groups
    .table-top
        display: block
        .row
            display: flex
            justify-content: space-between
.modal-footer
    display: flex
    justify-content: flex-end
</style>
